import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import API_BASE from '../config';
import { creditAwareFetch } from '../hooks/creditAwareFetch';

const ALL_COUNTRIES = [
  'Afghanistan','Albania','Algeria','Andorra','Angola','Argentina','Armenia','Australia','Austria',
  'Azerbaijan','Bahamas','Bahrain','Bangladesh','Barbados','Belarus','Belgium','Belize','Benin',
  'Bhutan','Bolivia','Bosnia and Herzegovina','Botswana','Brazil','Brunei','Bulgaria','Burkina Faso',
  'Burundi','Cambodia','Cameroon','Canada','Chad','Chile','China','Colombia','Costa Rica',
  "Cote d'Ivoire",'Croatia','Cuba','Cyprus','Czech Republic','Denmark','Dominican Republic',
  'Ecuador','Egypt','El Salvador','Estonia','Ethiopia','Fiji','Finland','France','Gabon','Gambia',
  'Georgia','Germany','Ghana','Greece','Guatemala','Guinea','Guyana','Haiti','Honduras','Hong Kong',
  'Hungary','Iceland','India','Indonesia','Iraq','Ireland','Israel','Italy','Jamaica','Japan',
  'Jordan','Kazakhstan','Kenya','Kuwait','Kyrgyzstan','Laos','Latvia','Lebanon','Lesotho','Liberia',
  'Libya','Liechtenstein','Lithuania','Luxembourg','Madagascar','Malawi','Malaysia','Maldives',
  'Mali','Malta','Mauritania','Mauritius','Mexico','Moldova','Monaco','Mongolia','Montenegro',
  'Morocco','Mozambique','Myanmar','Namibia','Nepal','Netherlands','New Zealand','Nicaragua',
  'Niger','Nigeria','North Macedonia','Norway','Oman','Pakistan','Panama','Papua New Guinea',
  'Paraguay','Peru','Philippines','Poland','Portugal','Qatar','Romania','Rwanda','Saudi Arabia',
  'Senegal','Serbia','Seychelles','Sierra Leone','Singapore','Slovakia','Slovenia','Somalia',
  'South Africa','South Korea','Spain','Sri Lanka','Sudan','Sweden','Switzerland','Taiwan',
  'Tajikistan','Tanzania','Thailand','Togo','Trinidad and Tobago','Tunisia','Turkey','Turkmenistan',
  'Uganda','Ukraine','United Arab Emirates','United Kingdom','United States','Uruguay','Uzbekistan',
  'Venezuela','Vietnam','Yemen','Zambia','Zimbabwe'
];

const REGIONAL_SHORTLIST = {
  'United Kingdom': ['Ireland','Netherlands','Germany','France','Belgium','Sweden','Denmark','Norway'],
  'Ireland': ['United Kingdom','Netherlands','Germany','France','Belgium'],
  'United States': ['Canada','Mexico','United Kingdom','Australia'],
  'Canada': ['United States','United Kingdom','Mexico'],
  'Nigeria': ['Ghana','Kenya','South Africa',"Cote d'Ivoire",'Senegal','Cameroon','Egypt','Rwanda'],
  'Ghana': ['Nigeria',"Cote d'Ivoire",'Senegal','Kenya','South Africa'],
  'Kenya': ['Nigeria','Tanzania','Uganda','Rwanda','South Africa','Ethiopia'],
  'South Africa': ['Nigeria','Kenya','Botswana','Namibia','Zimbabwe','Mauritius'],
  'Egypt': ['United Arab Emirates','Saudi Arabia','Morocco','Jordan','Tunisia'],
  'Germany': ['Netherlands','France','Switzerland','Austria','Belgium','Poland','Denmark'],
  'France': ['Belgium','Switzerland','Germany','Spain','Italy','Luxembourg'],
  'Netherlands': ['Belgium','Germany','Luxembourg','United Kingdom','France'],
  'Spain': ['Portugal','France','Italy','Andorra'],
  'Italy': ['Switzerland','France','Austria','Slovenia'],
  'India': ['Sri Lanka','Bangladesh','Nepal','Bhutan','United Arab Emirates','Singapore'],
  'United Arab Emirates': ['Saudi Arabia','Qatar','Bahrain','Oman','Kuwait','Egypt','India'],
  'Australia': ['New Zealand','Singapore','United States','United Kingdom'],
  'Singapore': ['Malaysia','Indonesia','Thailand','Vietnam','Philippines','Hong Kong','Australia'],
  'Brazil': ['Argentina','Uruguay','Paraguay','Chile','Colombia'],
  'Mexico': ['United States','Guatemala','Belize','Costa Rica','Colombia']
};

function inferCountry(locationStr) {
  if (!locationStr) return null;
  const s = locationStr.toLowerCase();
  for (const c of ALL_COUNTRIES) {
    if (s.includes(c.toLowerCase())) return c;
  }
  const alternates = { 'uk': 'United Kingdom', 'usa': 'United States', 'us': 'United States', 'uae': 'United Arab Emirates' };
  for (const [k, v] of Object.entries(alternates)) {
    if (s.includes(k)) return v;
  }
  return null;
}

export default function FindSimilarContacts() {
  const { id: clientId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const seedFromUrl = searchParams.get('seed');

  const [contacts, setContacts] = useState([]);
  const [seedId, setSeedId] = useState(seedFromUrl ? parseInt(seedFromUrl, 10) : null);
  const [seed, setSeed] = useState(null);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const [mode, setMode] = useState('reuse'); // 'reuse' or 'fresh'
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationSearch, setLocationSearch] = useState('');

  // Load contacts for the picker
  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API_BASE}/clients/${clientId}/contacts`);
        const data = await res.json();
        const list = Array.isArray(data) ? data : (data.contacts || []);
        // Only show contacts that have a job title and a company — required for find-similar
        const valid = list.filter(c => c.job_title && c.company_id);
        setContacts(valid);
        if (seedId) {
          const found = valid.find(c => c.id === seedId);
          if (found) setSeed(found);
        }
      } catch (e) {
        toast.error('Could not load contacts');
      }
    }
    load();
  }, [clientId, seedId]);

  // When fresh mode + seed loaded, default location to seed's company location
  useEffect(() => {
    if (!seed || mode !== 'fresh') return;
    const inferred = inferCountry(seed.company_location);
    if (inferred) setSelectedLocations([inferred]);
    else setSelectedLocations([]);
  }, [seed, mode]);

  const seedCountry = seed ? inferCountry(seed.company_location) : null;
  const DEFAULT_SHORTLIST = ['United Kingdom','Ireland','United States','Germany','France','Netherlands','Singapore','United Arab Emirates'];
  const shortlist = (seedCountry && REGIONAL_SHORTLIST[seedCountry]) ? REGIONAL_SHORTLIST[seedCountry] : DEFAULT_SHORTLIST;

  function toggleLocation(country) {
    setSelectedLocations(prev =>
      prev.includes(country) ? prev.filter(c => c !== country) : [...prev, country]
    );
  }

  function removeLocation(country) {
    setSelectedLocations(prev => prev.filter(c => c !== country));
  }

  function clearLocations() {
    setSelectedLocations([]);
  }

  const searchMatches = locationSearch.trim().length >= 2
    ? ALL_COUNTRIES
        .filter(c => c.toLowerCase().includes(locationSearch.toLowerCase()))
        .filter(c => !selectedLocations.includes(c))
        .slice(0, 8)
    : [];

  // Cost preview for the user
  const costPreview = mode === 'reuse' ? 2 : 3;
  const costNote = mode === 'reuse'
    ? '2 credits — uses existing prospect companies in your workspace'
    : '3 credits — fresh search for lookalike companies';

  async function runSearch() {
    if (!seedId) {
      toast.error('Pick a seed contact first');
      return;
    }
    setSearching(true);
    setResults(null);
    setSelected(new Set());
    try {
      const body = { mode };
      if (mode === 'fresh') body.locations = selectedLocations;
      const res = await creditAwareFetch(`${API_BASE}/contacts/${seedId}/find-similar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res) { setSearching(false); return; }
      const data = await res.json();
      if (data.error) throw new Error(data.error);

      // Filter to only rows with a contact suggestion
      const filtered = { ...data, results: (data.results || []).filter(r => r.contact_suggestion) };
      setResults(filtered);

      if (filtered.results.length === 0) {
        toast('No contact matches found in this batch.', { icon: 'ℹ' });
      }
    } catch (e) {
      toast.error('Could not find similar: ' + e.message);
    }
    setSearching(false);
  }

  function toggleSelected(idx) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  }

  function selectAll() {
    if (!results) return;
    if (selected.size === results.results.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(results.results.slice(0, 10).map((_, i) => i)));
    }
  }

  async function importSelected() {
    if (!selected.size) return;
    if (selected.size > 10) {
      toast.error('Max 10 per batch');
      return;
    }
    setImporting(true);
    const toastId = toast.loading(`Adding ${selected.size} ${selected.size === 1 ? 'prospect' : 'prospects'}...`);
    try {
      const pairs = Array.from(selected).map(idx => ({
        company: results.results[idx].company,
        contact: results.results[idx].contact_suggestion
      }));
      const res = await creditAwareFetch(`${API_BASE}/clients/${clientId}/find-similar-contacts/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pairs })
      });
      if (!res) { setImporting(false); return; }
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      toast.success(`Imported ${data.count} ${data.count === 1 ? 'prospect' : 'prospects'}${data.skipped_count ? ` (${data.skipped_count} skipped)` : ''}`, { id: toastId });
      // Mark imported rows
      setResults(prev => ({
        ...prev,
        results: prev.results.map((r, i) => selected.has(i) ? { ...r, _imported: true } : r)
      }));
      setSelected(new Set());
    } catch (e) {
      toast.error('Import failed: ' + e.message, { id: toastId });
    }
    setImporting(false);
  }

  const chipStyle = (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
    padding: '0.3rem 0.7rem', fontSize: '0.78rem', borderRadius: '20px',
    border: '0.5px solid ' + (active ? '#8B1A1A' : '#E8E4E0'),
    background: active ? '#FFF5F5' : '#FFF',
    color: active ? '#8B1A1A' : '#4A5568',
    cursor: 'pointer', fontWeight: active ? '500' : '400'
  });

  return (
    <div style={{ padding: '2rem', maxWidth: '1100px', margin: '0 auto' }}>
      <button onClick={() => navigate(`/clients/${clientId}/prospects`)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.85rem', cursor: 'pointer', marginBottom: '1rem' }}>← Back to prospects</button>

      <h1 style={{ fontSize: '1.5rem', fontWeight: '500', margin: '0 0 0.3rem', color: '#1A1A1A' }}>Find similar contacts</h1>
      <p style={{ margin: '0 0 1.5rem', fontSize: '0.85rem', color: '#888' }}>
        Pick a contact you want more of. We'll find people in the same role at lookalike companies.
      </p>

      {/* Seed contact picker */}
      <div style={{ background: '#FFF', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.25rem', marginBottom: '1rem' }}>
        <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: '600', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Seed contact</label>
        <select
          value={seedId || ''}
          onChange={e => {
            const v = e.target.value ? parseInt(e.target.value, 10) : null;
            setSeedId(v);
            const found = contacts.find(c => c.id === v);
            setSeed(found || null);
          }}
          style={{ width: '100%', padding: '0.6rem 0.8rem', fontSize: '0.9rem', border: '0.5px solid #E8E4E0', borderRadius: '6px', backgroundColor: '#fff' }}>
          <option value="">— pick a contact —</option>
          {contacts.map(c => (
            <option key={c.id} value={c.id}>{c.full_name} — {c.job_title} at {c.company_name}</option>
          ))}
        </select>
      </div>

      {/* Mode toggle */}
      <div style={{ background: '#FFF', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.25rem', marginBottom: '1rem' }}>
        <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: '600', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Search mode</label>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <button onClick={() => setMode('reuse')} style={chipStyle(mode === 'reuse')}>
            Reuse existing prospects
          </button>
          <button onClick={() => setMode('fresh')} style={chipStyle(mode === 'fresh')}>
            Fresh search
          </button>
        </div>
        <p style={{ margin: 0, fontSize: '0.78rem', color: '#888' }}>{costNote}</p>
      </div>

      {/* Locations panel — only in fresh mode */}
      {mode === 'fresh' && (
        <div style={{ background: '#FFF', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.25rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem' }}>
            <label style={{ fontSize: '0.72rem', fontWeight: '600', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Locations {selectedLocations.length === 0 && <span style={{ fontWeight: '400', color: '#888', textTransform: 'none', letterSpacing: 'normal' }}>· searching globally</span>}
            </label>
            {selectedLocations.length > 0 && (
              <button onClick={clearLocations} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.75rem', cursor: 'pointer' }}>
                Clear all (search globally)
              </button>
            )}
          </div>

          {selectedLocations.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.75rem' }}>
              {selectedLocations.map(c => (
                <span key={c} style={chipStyle(true)}>
                  {c}
                  <button onClick={() => removeLocation(c)} style={{ background: 'none', border: 'none', color: '#8B1A1A', fontSize: '0.95rem', cursor: 'pointer', padding: 0, marginLeft: '0.15rem', lineHeight: 1 }}>×</button>
                </span>
              ))}
            </div>
          )}

          {shortlist.length > 0 && shortlist.some(c => !selectedLocations.includes(c)) && (
            <div style={{ marginBottom: '0.75rem' }}>
              <p style={{ margin: '0 0 0.4rem', fontSize: '0.72rem', color: '#888', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                Suggested markets:
                <span title={seedCountry ? 'Markets commonly prospected alongside ' + seedCountry + ' — grouped by trade flows and business proximity, not pure geography.' : 'A default starter set of markets you commonly prospect into. Set the seed company\'s location to get tailored suggestions.'}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '14px', height: '14px', borderRadius: '50%', border: '0.5px solid #BBB', color: '#888', fontSize: '0.65rem', cursor: 'help', fontWeight: '500' }}>?</span>
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {shortlist.filter(c => !selectedLocations.includes(c)).map(c => (
                  <button key={c} onClick={() => toggleLocation(c)} style={chipStyle(false)}>
                    + {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <input
              type="text"
              value={locationSearch}
              onChange={e => setLocationSearch(e.target.value)}
              placeholder="Search any country..."
              style={{ width: '100%', padding: '0.5rem 0.7rem', fontSize: '0.85rem', border: '0.5px solid #E8E4E0', borderRadius: '6px' }}
            />
            {searchMatches.length > 0 && (
              <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {searchMatches.map(c => (
                  <button key={c} onClick={() => { toggleLocation(c); setLocationSearch(''); }} style={chipStyle(false)}>
                    + {c}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search button + cost */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        <button onClick={runSearch} disabled={!seedId || searching} style={{ fontSize: '0.9rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.7rem 1.5rem', cursor: (!seedId || searching) ? 'not-allowed' : 'pointer', opacity: (!seedId || searching) ? 0.6 : 1, fontWeight: '500' }}>
          {searching ? 'Searching...' : `Find similar (${costPreview} credits) →`}
        </button>
      </div>

      {/* Results */}
      {results && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: '500', margin: 0, color: '#1A1A1A' }}>
              {results.results.length} contact {results.results.length === 1 ? 'match' : 'matches'} for <span style={{ color: '#8B1A1A' }}>{seed?.full_name}</span>
              {results.locations_applied && results.locations_applied.length > 0 && (
                <span style={{ fontSize: '0.78rem', color: '#888', fontWeight: '400', marginLeft: '0.5rem' }}>
                  in {results.locations_applied.join(', ')}
                </span>
              )}
            </h2>
            {results.results.length > 0 && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={selectAll} style={{ fontSize: '0.78rem', color: '#4A5568', background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '6px', padding: '0.4rem 0.8rem', cursor: 'pointer' }}>
                  {selected.size === results.results.length ? 'Deselect all' : `Select all ${results.results.length}`}
                </button>
                <button onClick={importSelected} disabled={!selected.size || importing} style={{ fontSize: '0.78rem', color: '#fff', background: '#276749', border: 'none', borderRadius: '6px', padding: '0.4rem 0.8rem', cursor: (!selected.size || importing) ? 'not-allowed' : 'pointer', opacity: (!selected.size || importing) ? 0.6 : 1, fontWeight: '500' }}>
                  {importing ? 'Importing...' : `Add ${selected.size} to prospects`}
                </button>
              </div>
            )}
          </div>

          {results.results.length === 0 ? (
            <div style={{ background: '#FAFAF8', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '2rem', textAlign: 'center', color: '#888', fontSize: '0.88rem' }}>
              No contact matches found in this batch. Try fresh mode for a wider search.
            </div>
          ) : (
            <div style={{ background: '#FFF', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden' }}>
              {results.results.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '1rem 1.25rem', borderTop: i > 0 ? '0.5px solid #F0EDE9' : 'none', opacity: r._imported ? 0.5 : 1 }}>
                  <input
                    type="checkbox"
                    checked={selected.has(i)}
                    onChange={() => toggleSelected(i)}
                    disabled={r._imported}
                    style={{ marginTop: '0.25rem', cursor: r._imported ? 'not-allowed' : 'pointer' }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: '500', color: '#1A1A1A' }}>{r.contact_suggestion.name}</p>
                      <span style={{ fontSize: '0.78rem', color: '#4A5568' }}>· {r.contact_suggestion.title}</span>
                      {r.contact_suggestion.linkedin_url && (
                        <a href={r.contact_suggestion.linkedin_url} target="_blank" rel="noreferrer" style={{ fontSize: '0.75rem', color: '#1A56A0', textDecoration: 'none' }}>LinkedIn ↗</a>
                      )}
                      {r._imported && <span style={{ fontSize: '0.7rem', color: '#276749', background: '#F0FFF4', padding: '0.1rem 0.5rem', borderRadius: '20px' }}>Imported</span>}
                    </div>
                    <p style={{ margin: '0 0 0.3rem', fontSize: '0.82rem', color: '#4A5568' }}>
                      <strong>{r.company.name}</strong>
                      {r.company.location && <span style={{ color: '#888' }}> · {r.company.location}</span>}
                    </p>
                    {r.company.description && (
                      <p style={{ margin: 0, fontSize: '0.78rem', color: '#888', lineHeight: '1.5' }}>{r.company.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
